import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { LocalStorageService } from './local-storage.service';
import { ConfigService } from './config.service';
import { Log, IShortLogger } from './logger.service';

/**
 * Manages per-user UI preferences backed by localStorage.
 *
 * **Key format**: `{page}:{scope}:{action}`
 * e.g. `templateSubscriptions:banner:dismissed`
 *
 * **Storage layout**: one JSON blob per userId under the localStorage key
 * `userPrefs:{userId}` containing a flat `Record<prefKey, value>`.
 *
 * **Lifecycle**:
 * - `prime(userId, serverPrefs?)` — called by AuthService on login / token refresh.
 *   Server-side prefs are merged in (server wins for same keys).
 * - `clear()` — called by AuthService on logout.
 *
 * **Backend sync**: `set()` fire-and-forgets a `PUT /api/v1/user-preferences` so the
 * backend collection stays in sync for future logins.
 *
 * Components must only interact with this service, never with storage directly.
 */
@Injectable({ providedIn: 'root' })
export class UserPrefsService {
  private readonly logger: IShortLogger<UserPrefsService> = Log.create(UserPrefsService);
  private userId: string | null = null;

  constructor(
    private readonly storage: LocalStorageService,
    private readonly http: HttpClient,
    private readonly configService: ConfigService
  ) {}

  /**
   * Seeds the in-memory userId and merges server-side preferences into local storage.
   * Must be called during the login / token-refresh flow before any component reads prefs.
   */
  prime(userId: string, serverPrefs: Record<string, unknown> = {}): void {
    this.userId = userId;
    const local = this.getAll();
    const merged = { ...local, ...serverPrefs };
    this.storage.setItem(this.storageKey(userId), merged);
  }

  /**
   * Clears all locally stored preferences for the current user and resets the userId.
   * Called by AuthService on logout — do NOT call localStorage.clear() yourself, the
   * auth service already does so; this method simply resets the service state.
   */
  clear(): void {
    if (this.userId) {
      this.storage.removeItem(this.storageKey(this.userId));
    }
    this.userId = null;
  }

  /**
   * Retrieves a single preference value, or `null` if not set.
   * @param page   Top-level page/feature key (e.g. `'templateSubscriptions'`)
   * @param scope  Preference scope (e.g. `'banner'`, `'view'`)
   * @param action Specific action (e.g. `'dismissed'`, `'mode'`)
   */
  get<T>(page: string, scope: string, action: string): T | null {
    if (!this.userId) return null;
    const prefs = this.getAll();
    const key = this.buildKey(page, scope, action);
    const value = prefs[key];
    return value !== undefined ? (value as T) : null;
  }

  /**
   * Stores a preference and asynchronously syncs it to the backend.
   */
  set(page: string, scope: string, action: string, value: unknown): void {
    if (!this.userId) {
      this.logger.w('set() called before prime() — preference not saved', page, scope, action);
      return;
    }
    const prefs = this.getAll();
    prefs[this.buildKey(page, scope, action)] = value;
    this.storage.setItem(this.storageKey(this.userId), prefs);
    this.syncToBackend(prefs);
  }

  // ── Convenience helpers ─────────────────────────────────────────────────

  /**
   * Returns true if the named banner has been permanently dismissed by this user.
   * @param page     Page/feature identifier (e.g. `'templateSubscriptions'`)
   * @param bannerId Optional banner discriminator when a page has multiple banners (default `'main'`)
   */
  isBannerDismissed(page: string, bannerId = 'main'): boolean {
    return this.get<boolean>(page, 'banner', `dismissed:${bannerId}`) === true;
  }

  /**
   * Marks the banner as permanently dismissed and persists the preference.
   */
  dismissBanner(page: string, bannerId = 'main'): void {
    this.set(page, 'banner', `dismissed:${bannerId}`, true);
  }

  /**
   * Removes a single preference key locally and syncs the change to the backend.
   */
  remove(page: string, scope: string, action: string): void {
    if (!this.userId) return;
    const prefs = this.getAll();
    delete prefs[this.buildKey(page, scope, action)];
    this.storage.setItem(this.storageKey(this.userId), prefs);
    this.syncToBackend(prefs);
  }

  /**
   * Wipes ALL preferences for the current user — both local storage and backend.
   * Intended for the "Reset User Preferences" action in Profile > Settings.
   */
  reset(): void {
    if (!this.userId) return;
    this.storage.setItem(this.storageKey(this.userId), {});
    this.syncToBackend({});
  }

  // ── Private helpers ─────────────────────────────────────────────────────

  private buildKey(page: string, scope: string, action: string): string {
    return `${page}:${scope}:${action}`;
  }

  private storageKey(userId: string): string {
    return `userPrefs:${userId}`;
  }

  private getAll(): Record<string, unknown> {
    if (!this.userId) return {};
    return this.storage.getItem<Record<string, unknown>>(this.storageKey(this.userId)) ?? {};
  }

  /** Fire-and-forget sync to backend — never blocks UI. */
  private syncToBackend(prefs: Record<string, unknown>): void {
    const url = `${this.configService.getApiUrl()}/v1/user-preferences`;
    this.http.put(url, { preferences: prefs }).subscribe({
      error: (err) => this.logger.w('Background pref sync failed:', err?.message ?? err)
    });
  }
}
